import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { Controller } from 'react-hook-form';
import useAuthState from "@/states/auth.state";
import useTenantState from "@/states/tenant.state";
import { useParams } from "react-router-dom";
import { injectParametersIntoHtml } from '../RenderCode/utils/base64Utils';
import { AlurkerjaInputType } from '../../types/InputType';
import { env } from '@/utils/constants/env';

export interface ShadowEmbedProps {
  props: AlurkerjaInputType;
  directHtml?: string; // Alternative untuk load HTML langsung tanpa fetch
}

type FormElement = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

// Utility functions untuk membaca nilai elemen form dari Shadow DOM
function readInputValue(el: HTMLInputElement) {
  const type = el.type.toLowerCase();

  if (type === "checkbox" || type === "radio") {
    return el.checked ? el.value : undefined;
  } else if (type === "file") {
    return el.files ? Array.from(el.files) : [];
  } else {
    return el.value;
  }
}

function readSelectValue(el: HTMLSelectElement) {
  if (el.multiple) {
    return Array.from(el.selectedOptions).map(opt => opt.value);
  }
  return el.value;
}

function readElementValue(el: FormElement) {
  if (el instanceof HTMLInputElement) {
    return readInputValue(el);
  } else if (el instanceof HTMLSelectElement) {
    return readSelectValue(el);
  } else if (el instanceof HTMLTextAreaElement) {
    return el.value;
  }
  return undefined;
}

// Extract semua nilai dari form dalam Shadow DOM (hanya yang ada nilai)
function extractAllValues(shadowRoot: ShadowRoot) {
  const fields = shadowRoot.querySelectorAll<FormElement>(
    "input[name], select[name], textarea[name]"
  );

  const data: Record<string, any> = {};

  for (const el of fields) {
    const name = el.getAttribute("name");
    if (!name || el.hasAttribute("disabled")) continue;

    const value = readElementValue(el);
    // Hanya tambahkan jika ada nilai yang meaningful
    if (value !== undefined && value !== '' && value !== null) {
      data[name] = value;
    }
  }

  return data;
}

// Sync semua field dari Shadow DOM ke React Hook Form
function syncAllFieldsToRHF(shadowRoot: ShadowRoot, setValue: (n: string, v: any) => void , currentFields : string[] , setCurrentFields:any ) {
  const data = extractAllValues(shadowRoot);
  for (const [k, v] of Object.entries(data)) {
    if(!currentFields.includes(k)) {
      setCurrentFields([...currentFields, k]);
    }

    for(const field of currentFields) {
      if(!data.hasOwnProperty(field)) {
        setValue(field, undefined);
      }else {
        setValue(k, v);
      }
    
    }
  }
}

// Sync data dari React Hook Form ke Shadow DOM elements
function updateInputElement(el: HTMLInputElement, value: any, isArrayName: boolean) {
  const type = el.type.toLowerCase();

  if (type === "checkbox") {
    if (isArrayName && Array.isArray(value)) {
      el.checked = value.includes(el.value);
    } else {
      el.checked = Boolean(value);
    }
  } else if (type === "radio") {
    el.checked = el.value === value;
  } else if (type === "file") {
    // File inputs tidak bisa diset secara programatis
    return;
  } else {
    el.value = String(value || '');
  }
}

function updateSelectElement(el: HTMLSelectElement, value: any) {
  if (el.multiple && Array.isArray(value)) {
    for (const option of el.options) {
      option.selected = value.includes(option.value);
    }
  } else {
    el.value = String(value || '');
  }
}

function syncRHFToAllFields(shadowRoot: ShadowRoot, formData: Record<string, any>) {
  const fields = shadowRoot.querySelectorAll<FormElement>(
    "input[name]:not([disabled]), select[name]:not([disabled]), textarea[name]:not([disabled])"
  );

  for (const el of fields) {
    const name = el.getAttribute("name")!;
    const isArrayName = name.endsWith('[]');
    const key = isArrayName ? name.replace(/\[\]$/, "") : name;
    const value = formData[key];

    if (value === undefined) continue;

    if (el instanceof HTMLInputElement) {
      updateInputElement(el, value, isArrayName);
    } else if (el instanceof HTMLSelectElement) {
      updateSelectElement(el, value);
    } else if (el instanceof HTMLTextAreaElement) {
      el.value = String(value || '');
    }
  }
}

// API untuk setValue dan getValue dari embed
interface EmbedFormAPI {
  setValue: (name: string, value: any) => void;
  getValue: (name: string) => any;
  getAllValues: () => Record<string, any>;
}

// Script execution helper dengan Form API injection
function executeScriptInShadowContext(
  scriptContent: string,
  shadowRoot: ShadowRoot,
  formAPI: EmbedFormAPI
): void {
  try {
    // Create script with shadow DOM context dan Form API
    const scriptFunction = new Function(
      'document', 'window', 'alert', 'console', 'FormAPI', 'shadowRoot',
      `
        // Override document methods untuk shadow DOM context
        const originalGetElementById = document.getElementById;
        const originalAddEventListener = document.addEventListener;
        const originalQuerySelector = document.querySelector;
        const originalQuerySelectorAll = document.querySelectorAll;
        const originalAlert = globalThis.alert;
        
        document.getElementById = function(id) {
          return shadowRoot.getElementById(id) || originalGetElementById.call(document, id);
        };
        
        document.querySelector = function(selector) {
          return shadowRoot.querySelector(selector) || originalQuerySelector.call(document, selector);
        };
        
        document.querySelectorAll = function(selector) {
          const shadowResults = shadowRoot.querySelectorAll(selector);
          if (shadowResults.length > 0) {
            return shadowResults;
          }
          return originalQuerySelectorAll.call(document, selector);
        };
        
        // Override addEventListener untuk menangani DOMContentLoaded
        document.addEventListener = function(event, handler, options) {
          if (event === 'DOMContentLoaded') {
            // DOMContentLoaded sudah terlewat di Shadow DOM, langsung eksekusi
            setTimeout(() => {
              try {
                if (typeof handler === 'function') {
                  handler();
                } else if (handler && typeof handler.handleEvent === 'function') {
                  handler.handleEvent(new Event('DOMContentLoaded'));
                }
              } catch (error) {
                console.error('DOMContentLoaded handler error:', error);
              }
            }, 0);
          } else {
            // Event lain, gunakan shadowRoot sebagai target
            return shadowRoot.addEventListener(event, handler, options);
          }
        };
        
        globalThis.alert = function(message) {
          return originalAlert(message);
        };
        
        // Inject Form API ke global scope
        globalThis.FormAPI = FormAPI;
        window.FormAPI = FormAPI;
        
        // Execute the original script dengan proper scope
        try {
          ${scriptContent}
        } catch (scriptError) {
          console.error('Script execution error:', scriptError);
          throw scriptError;
        }
        
        // Restore original methods
        document.getElementById = originalGetElementById;
        document.querySelector = originalQuerySelector;
        document.querySelectorAll = originalQuerySelectorAll;
        document.addEventListener = originalAddEventListener;
        globalThis.alert = originalAlert;
      `
    );

    // Execute with shadow DOM context dan Form API
    scriptFunction.call(shadowRoot, shadowRoot, globalThis, alert, console, formAPI, shadowRoot);

  } catch (error) {
    console.error('Shadow script execution error:', error);
  }
}

const ShadowEmbed: React.FC<ShadowEmbedProps> = ({ props, directHtml }) => {
  const { item, form: { control, setValue, watch } } = props;
  const { currentTenant } = useTenantState();
  const params = useParams();
  const [token, setToken] = useState<string | null>(null);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [shadowReady, setShadowReady] = useState<boolean>(false);

  // Fetch token on mount
  useEffect(() => {
    const fetchToken = async () => {
      const authToken = await useAuthState.getState().getToken();
      setToken(authToken);
    };
    fetchToken();
  }, []);

  const shadowRootRef = useRef<ShadowRoot | null>(null);
  const [containerElement, setContainerElement] = useState<HTMLDivElement | null>(null);

  const [currentFields, setCurrentFields] = useState<string[]>([]);

  // Watch all form values untuk sync
  const watchedValues = watch();

  const url = props.item.shadow_embed_id;
  const x_active_tenant = currentTenant?.slug;
  const probis = params.processKey;
  const x_task_id = params.id;

  const finalUrl = (url || "")?.startsWith(`http`) ? url : `${env.cloudStorage}${url}`;

  // Prepare parameters for injection
  const injectionParams = useMemo(() => ({
    'token': token || '',
    'x-active-tenant': x_active_tenant || '',
    'probis': probis || '',
    'task_id': x_task_id || '',
  }), [token, x_active_tenant, probis, x_task_id]);

  // Function to fetch content and render in Shadow DOM dengan Form API
  const fetchAndRenderInShadow = useCallback(async () => {
    setLoading(true);
    setError(null);
    setShadowReady(false);

    try {
      let html = '';

      // Prioritas menggunakan directHtml jika ada
      if (directHtml) {
        html = directHtml;
      } else {
        // Fallback ke fetch jika tidak ada directHtml
        const response = await fetch(finalUrl, {
          headers: {
            'Cache-Control': 'no-cache',
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        html = await response.text();
      }

      // Inject parameters into HTML content
      html = injectParametersIntoHtml(html, injectionParams);

      // Create Shadow DOM
      const container = containerElement;
      if (!container) {
        throw new Error('Container element not found');
      }

      shadowRootRef.current ??= container.attachShadow({ mode: 'open' });

      const shadowRoot = shadowRootRef.current;

      // Render HTML ke Shadow DOM
      shadowRoot.innerHTML = html;

      // Inject FormAPI helper script langsung ke Shadow DOM
      const formAPIScript = document.createElement('script');
      formAPIScript.textContent = `
        // --- FormAPI Helper ---
        (function () {
          if (typeof window.FormAPI !== "undefined") return;
          const form = document.querySelector("form");
          if (!form) {
            console.warn("FormAPI: No form found in Shadow DOM");
            return;
          }

          function qAll(sel) {
            return form.querySelectorAll(sel);
          }

          function setValue(name, value) {
            const isArray = name.endsWith("[]");
            const base = isArray ? name.slice(0, -2) : name;

            // checkbox group
            const group = qAll(\`input[type="checkbox"][name="\${base}[]"]\`);
            if (group.length) {
              const vals = Array.isArray(value) ? value.map(String) : [];
              group.forEach(cb => cb.checked = vals.includes(String(cb.value)));
              return;
            }

            // radio group
            const radios = qAll(\`input[type="radio"][name="\${base}"]\`);
            if (radios.length) {
              radios.forEach(r => r.checked = r.value === value);
              return;
            }

            const el = form.querySelector(\`[name="\${base}"]\`);
            if (!el) return;
            if (el.type === "checkbox") el.checked = Boolean(value);
            else el.value = value ?? "";
          }

          function getValue(name) {
            const isArray = name.endsWith("[]");
            const base = isArray ? name.slice(0, -2) : name;

            const group = qAll(\`input[type="checkbox"][name="\${base}[]"]\`);
            if (group.length) {
              return Array.from(group).filter(cb => cb.checked).map(cb => cb.value);
            }

            const radios = qAll(\`input[type="radio"][name="\${base}"]\`);
            if (radios.length) {
              const selected = Array.from(radios).find(r => r.checked);
              return selected ? selected.value : "";
            }

            const el = form.querySelector(\`[name="\${base}"]\`);
            if (!el) return "";
            return el.type === "checkbox" ? el.checked : el.value;
          }

          function getAllValues() {
            const data = {};
            const fields = form.querySelectorAll("[name]");
            fields.forEach(f => {
              const n = f.name;
              if (/\\[\\]$/.test(n)) {
                const base = n.slice(0, -2);
                if (!data.hasOwnProperty(base)) {
                  data[base] = getValue(n);
                }
              } else {
                data[n] = getValue(n);
              }
            });
            return data;
          }

          window.FormAPI = { setValue, getValue, getAllValues };
        })();
      `;
      shadowRoot.appendChild(formAPIScript);

      // Create Form API untuk embed (tetap ada untuk integration dengan RHF)
      const formAPI: EmbedFormAPI = {
        setValue: (name: string, value: any) => {
          setValue(name, value);

          // Update juga DOM element di shadow
          setTimeout(() => {
            const element = shadowRoot.querySelector<FormElement>(`[name="${name}"]`);
            if (element) {
              if (element instanceof HTMLInputElement) {
                updateInputElement(element, value, name.endsWith('[]'));
              } else if (element instanceof HTMLSelectElement) {
                updateSelectElement(element, value);
              } else if (element instanceof HTMLTextAreaElement) {
                element.value = String(value || '');
              }
            }
          }, 10);
        },

        getValue: (name: string) => {
          // Check apakah field ada di Shadow DOM
          const shadowElement = shadowRoot.querySelector<FormElement>(`[name="${name}"]`);
          const rhfValue = watchedValues[name];

          if (shadowElement) {
            const shadowValue = readElementValue(shadowElement);
            if (shadowValue !== undefined && shadowValue !== '' && shadowValue !== null) {
              return shadowValue;
            }
          }

          // Field tidak ada di Shadow DOM atau nilainya kosong, gunakan RHF
          return rhfValue;
        },

        getAllValues: () => {
          // Start dengan RHF values (untuk field yang diisi dari luar)
          const rhfValues = { ...watchedValues };

          // Get shadow DOM values
          const shadowValues = extractAllValues(shadowRoot);

          // Combine dengan prioritas: shadow DOM values override RHF values jika ada
          const combinedValues = { ...rhfValues };
          for (const [key, value] of Object.entries(shadowValues)) {
            combinedValues[key] = value;
          }

          return combinedValues;
        }
      };

      // Setup form sync dengan event delegation sederhana
      const setupFormSync = () => {
        // Initial sync from Shadow DOM to RHF
        syncAllFieldsToRHF(shadowRoot, setValue, currentFields , setCurrentFields );

        const onFormChange = (evt: Event) => {
          const target = evt.target as Element;
          const namedEl = target?.closest<FormElement>("[name]");
          if (!namedEl || namedEl.hasAttribute("disabled")) return;

          // Simple sync - get all values and sync to RHF
          setTimeout(() => {
            syncAllFieldsToRHF(shadowRoot, setValue, currentFields , setCurrentFields );
          }, 10);
        };

        // Add event listeners
        shadowRoot.addEventListener("input", onFormChange, true);
        shadowRoot.addEventListener("change", onFormChange, true);
      };

      // Execute scripts dengan Form API injection
      const executeScriptsWithAPI = () => {
        const scripts = shadowRoot.querySelectorAll('script');

        for (const script of scripts) {
          if (script.src) {
            // External script
            const newScript = document.createElement('script');
            newScript.src = script.src;
            shadowRoot.appendChild(newScript);
          } else {
            // Inline script dengan Form API injection
            const scriptContent = script.textContent || script.innerHTML;

            try {
              script.remove(); // Remove to prevent duplicate execution
              executeScriptInShadowContext(scriptContent, shadowRoot, formAPI);
            } catch (execError) {
              console.error('ShadowEmbed: Script execution error:', execError);
            }
          }
        }
      };

      // Execute scripts and setup sync
      setTimeout(() => {
        executeScriptsWithAPI();
        setupFormSync();
        setShadowReady(true);
        setLoading(false);
      }, 100);

    } catch (err) {
      console.error('ShadowEmbed: Failed to fetch and render shadow content:', err);
      setError(err instanceof Error ? err.message : String(err));
      setLoading(false);
    }
  }, [finalUrl, containerElement, injectionParams, setValue, watchedValues, directHtml]);

  // Handle messages dari Shadow DOM (simplified)
  useEffect(() => {
    const handleShadowMessage = (event: MessageEvent) => {
      if (event.data?.type === 'iframeReady') {
        setShadowReady(true);
      }
    };

    globalThis.addEventListener('message', handleShadowMessage);
    return () => {
      globalThis.removeEventListener('message', handleShadowMessage);
    };
  }, []);

  // Sync perubahan dari React Hook Form ke Shadow DOM
  useEffect(() => {
    if (shadowReady && shadowRootRef.current) {
      syncRHFToAllFields(shadowRootRef.current, watchedValues);
    }
  }, [watchedValues, shadowReady]);

  // Callback ref to capture container when available
  const setContainerRef = useCallback((element: HTMLDivElement | null) => {
    setContainerElement(element);
  }, []);

  // Trigger fetch when container and URL are ready
  useEffect(() => {
    if (containerElement && finalUrl && !shadowReady) {
      fetchAndRenderInShadow();
    }
  }, [containerElement, finalUrl, shadowReady, fetchAndRenderInShadow]);

  // Cleanup Shadow DOM saat unmount
  useEffect(() => {
    return () => {
      if (shadowRootRef.current && containerElement) {
        try {
          shadowRootRef.current.host.remove();
        } catch (error) {
          console.warn('Shadow root cleanup failed:', error);
        }
        shadowRootRef.current = null;
      }
    };
  }, [containerElement]);

  // Always render the container to ensure ref is set
  return (
    <>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <Controller
        name={item.name}
        control={control}
        rules={{
          required: item.constraints?.required ? 'This field is required' : false
        }}
        render={({ fieldState }) => {
          return (
            <div data-testid="shadow-embed-display">
              <div
                ref={setContainerRef}
                style={{
                  width: item.width ?? "100%",
                  // height: item.height ?? "500px",
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {/* Loading overlay */}
                {loading && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#f5f5f5',
                    zIndex: 1000
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ marginBottom: '10px' }}>Loading Shadow Embed...</div>
                      <div style={{ fontSize: '12px', marginBottom: '15px', color: '#666' }}>
                        URL: {finalUrl}
                      </div>
                      <div style={{
                        width: '20px',
                        height: '20px',
                        border: '2px solid #ccc',
                        borderTop: '2px solid #007bff',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        margin: '0 auto',
                        marginBottom: '15px'
                      }} />
                    </div>
                  </div>
                )}

                {/* Error overlay */}
                {error && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#f8f9fa',
                    zIndex: 1000
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ marginBottom: '10px', color: '#d63031' }}>Failed to load Shadow Embed</div>
                      <div style={{ fontSize: '12px', marginBottom: '15px', color: '#666' }}>
                        {error}
                      </div>
                      <button
                        onClick={() => {
                          setError(null);
                          setLoading(true);
                          fetchAndRenderInShadow();
                        }}
                        style={{
                          padding: '8px 16px',
                          background: '#d63031',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Retry
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Field error */}
              {fieldState.error && (
                <div style={{
                  color: '#d63031',
                  fontSize: '12px',
                  marginTop: '4px'
                }}>
                  {fieldState.error.message}
                </div>
              )}
            </div>
          );
        }}
      />
    </>
  );
};

export default ShadowEmbed;